// game/client/src/world/horizon.ts
//
// Sky dome (issue #4, 2026-06-18). A big BackSide sphere with a vertical gradient
// (zenith → horizon) replacing the flat grey background, so when the camera tilts
// or zooms you see a real sky rather than a flat clear-colour. The HORIZON end of
// the gradient is the live (lerped) fog colour, so the dome and the fog-dissolved
// terrain edge meet seamlessly. fog:false (a fogged sky is just flat fog) — the
// gradient is computed per-pixel from the view-ray direction.
//
// The "world continues past the edge" read is handled by the TERRAIN SKIRT
// (world/terrain.ts buildTerrainSkirt) — the perimeter is extruded outward into
// the fog so there's no visible cut. An earlier flat ground-apron disc + ridge
// ring lived here but read as "endless green fog" top-down; removed 2026-06-18.
//
// Recoloured each frame off the day/night lerp via update(), wired from
// day-night-visual.ts so the single lighting interpolation stays authoritative.

import * as THREE from "three";

export interface Horizon {
  /** Recolour for the current sky. `horizonColor` = the live (lerped) fog colour;
   *  `nightT` = 0 (day) … 1 (night). Cheap; call per frame. */
  update(horizonColor: THREE.Color, nightT: number): void;
}

const DAY_ZENITH = 0x5b93cf;
const NIGHT_ZENITH = 0x141d3a;

export function createHorizon(opts: { scene: THREE.Scene; mapUnits: number }): Horizon {
  const { scene, mapUnits } = opts;

  const skyUniforms = {
    uZenith: { value: new THREE.Color(DAY_ZENITH) },
    uHorizon: { value: new THREE.Color(0xc8d8e4) },
  };
  const skyMat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: skyUniforms,
    vertexShader: `
      varying vec3 vWorld;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorld = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: `
      uniform vec3 uZenith;
      uniform vec3 uHorizon;
      varying vec3 vWorld;
      void main() {
        vec3 dir = normalize(vWorld - cameraPosition);
        float h = smoothstep(-0.04, 0.55, dir.y);
        gl_FragColor = vec4(mix(uHorizon, uZenith, h), 1.0);
      }
    `,
  });
  const sky = new THREE.Mesh(new THREE.SphereGeometry(900, 32, 16), skyMat);
  sky.position.set(mapUnits / 2, 0, mapUnits / 2);
  sky.renderOrder = -10;
  sky.frustumCulled = false;
  scene.add(sky);

  const dayZenith = new THREE.Color(DAY_ZENITH);
  const nightZenith = new THREE.Color(NIGHT_ZENITH);

  function update(horizonColor: THREE.Color, nightT: number): void {
    skyUniforms.uHorizon.value.copy(horizonColor);
    skyUniforms.uZenith.value.copy(dayZenith).lerp(nightZenith, nightT);
  }

  return { update };
}
